import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const PAGE = 50;

export default function Transactions() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clinics, setClinics] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const [q, setQ] = useState('');
  const [clinicId, setClinicId] = useState(searchParams.get('clinic_id') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [type, setType] = useState('all'); // all | primary | repeat
  const [sortField, setSortField] = useState('paid_date');
  const [sortDir, setSortDir] = useState('desc');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  useEffect(() => {
    axios.get(`${API}/admin/clinics`, { headers, params: { limit: 2000 } }).then(r => setClinics(r.data.items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback((nextOffset) => {
    const params = { limit: PAGE, offset: nextOffset, sort: sortField, order: sortDir };
    if (q.trim()) params.q = q.trim();
    if (clinicId) params.clinic_id = clinicId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (type === 'primary') params.is_primary = true;
    if (type === 'repeat') params.is_primary = false;
    axios.get(`${API}/admin/transactions`, { headers, params }).then(r => {
      setItems(r.data.items);
      setTotal(r.data.total);
      setOffset(r.data.offset);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clinicId, dateFrom, dateTo, type, sortField, sortDir]);

  useEffect(() => { load(0); }, [load]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const arrow = (field) => (sortField !== field ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

  const apply = () => load(0);
  const reset = () => {
    setQ(''); setClinicId(''); setDateFrom(''); setDateTo(''); setType('all');
  };

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE < total;

  return (
    <Layout>
      <h1 style={styles.title}>Транзакции ({total.toLocaleString('ru')})</h1>

      <div style={styles.filters}>
        <input style={styles.input} placeholder="Поиск по ФИО врача/пациента"
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && apply()} />
        <select style={styles.select} value={clinicId} onChange={e => setClinicId(e.target.value)}>
          <option value="">Все клиники</option>
          {clinics.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={styles.select} value={type} onChange={e => setType(e.target.value)}>
          <option value="all">Все типы</option>
          <option value="primary">Первичные</option>
          <option value="repeat">Повторные</option>
        </select>
        <input type="date" style={styles.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span style={{ color: '#64748b' }}>—</span>
        <input type="date" style={styles.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        <button style={styles.btn} onClick={apply}>Применить</button>
        <button style={styles.btnGhost} onClick={reset}>Сброс</button>
      </div>

      <div style={styles.table}>
        <div style={{ ...styles.thead, gridTemplateColumns: cols }}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('paid_date')}>Дата оплаты{arrow('paid_date')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('amount')}>Сумма{arrow('amount')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('is_primary')}>Тип{arrow('is_primary')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('patient_name')}>Пациент{arrow('patient_name')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('doctor_name')}>Врач{arrow('doctor_name')}</div>
          <div style={styles.th}>ID врача</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('clinic_name')}>Клиника{arrow('clinic_name')}</div>
        </div>
        {items.map(t => (
          <div key={t.id} style={{ ...styles.row, gridTemplateColumns: cols }}>
            <div style={styles.td}>{new Date(t.paid_date).toLocaleDateString('ru')}</div>
            <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(t.amount)} ₽</div>
            <div style={styles.td}>
              {t.is_primary
                ? <span style={styles.badgeTeal}>Первичная</span>
                : <span style={styles.badgeGray}>Повторная</span>}
            </div>
            <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/patients/${t.patient_id}`)}>{t.patient_name}</div>
            <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/doctors/${t.doctor_id}`)}>{t.doctor_name}</div>
            <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>{t.doctor_public_id || '—'}</div>
            <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(t.target_type === 'network' ? `/networks/${t.target_id}` : `/clinics/${t.target_id || t.clinic_id}`)}>{t.clinic_name}</div>
          </div>
        ))}
        {items.length === 0 && <div style={styles.empty}>Ничего не найдено</div>}
      </div>

      <div style={styles.pager}>
        <span style={styles.pagerInfo}>{from}–{to} из {total.toLocaleString('ru')}</span>
        <button style={{ ...styles.pageBtn, ...(canPrev ? {} : styles.pageBtnDisabled) }}
          onClick={() => canPrev && load(offset - PAGE)} disabled={!canPrev}>← Назад</button>
        <button style={{ ...styles.pageBtn, ...(canNext ? {} : styles.pageBtnDisabled) }}
          onClick={() => canNext && load(offset + PAGE)} disabled={!canNext}>Вперёд →</button>
      </div>
    </Layout>
  );
}

const cols = '1.2fr 1fr 1fr 2fr 1.6fr 1fr 1.6fr';

const styles = {
  title: { color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 20, marginTop: 0 },
  filters: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  input: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none' },
  select: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none' },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', alignItems: 'center' },
  link: { color: '#2EC4B6', cursor: 'pointer' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '40px 0' },
  badgeTeal: { background: '#042f2e', color: '#2EC4B6', padding: '3px 8px', borderRadius: 20, fontSize: 12 },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, border: '1px solid #334155' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 },
  pagerInfo: { color: '#64748b', fontSize: 13, marginRight: 'auto' },
  pageBtn: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' },
  pageBtnDisabled: { opacity: 0.4, cursor: 'default' },
};
